function preserveGiftViewport(callback) {
  const y = window.scrollY;
  callback();
  const restore = () => {
    if (Math.abs(window.scrollY - y) > 4) {
      window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    }
  };
  requestAnimationFrame(() => requestAnimationFrame(restore));
  setTimeout(restore, 80);
}

const giftDrafts = new Map();
const legacyDeviceDrafts = new WeakMap();
const GIFT_SESSION_KEY = 'auzi.gift.session.v1';

function readGiftJourney() {
  try {
    const value = JSON.parse(sessionStorage.getItem(GIFT_SESSION_KEY) || 'null');
    if (!value || value.active !== true) return null;
    return value;
  } catch {
    return null;
  }
}

function writeGiftJourney(value) {
  try {
    if (value?.active) sessionStorage.setItem(GIFT_SESSION_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(GIFT_SESSION_KEY);
  } catch {}
}

function syncLegacyDeviceFlow() {
  // Retired: the native product device slot remains visible and owns the
  // selected gift/personal device state.
}

class AuziGiftOption extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.toggle = this.querySelector('[data-ad-gift-toggle]');
    this.nameInput = this.querySelector('[data-gift-name]');
    this.messageInput = this.querySelector('[data-gift-message]');
    this.titleElement = this.querySelector('[data-gift-title]');
    this.details = this.querySelector('.auzi-gift');
    if (!this.toggle || !this.details) return;

    const draft = giftDrafts.get(this.dataset.product);
    const journey = readGiftJourney();
    const params = new URLSearchParams(window.location.search);
    const arrivedAsGift = params.get('gift') === '1';
    const inGiftFlow = Boolean(arrivedAsGift || draft?.gift || journey?.active);

    this.hidden = false;
    document.querySelectorAll('[data-auzi-gift-only]').forEach(element => {
      element.hidden = !inGiftFlow;
    });

    if (draft) {
      this.toggle.checked = draft.gift;
      if (this.nameInput) this.nameInput.value = draft.name;
      if (this.messageInput) this.messageInput.value = draft.message;

    } else if (arrivedAsGift || journey?.active) {
      preserveGiftViewport(() => {
        this.toggle.checked = true;
        if (this.nameInput && journey?.recipientName) this.nameInput.value = journey.recipientName;
        if (this.messageInput && journey?.message) this.messageInput.value = journey.message;

        this.classList.add('auzi-gift-journey');
        this.details.setAttribute('data-gift-auto-opened', 'true');
      });
    }

    const options = { signal: this.abort.signal };
    this.addEventListener('input', () => this.save(), options);
    this.addEventListener('change', () => this.save(), options);

    this.update();
    queueMicrotask(() => this.syncNativeDeferredGiftState());
    window.addEventListener('load', () => this.syncNativeDeferredGiftState(), options);
    window.addEventListener('pageshow', () => this.syncNativeDeferredGiftState(), options);
    // If another deferred script initializes after this one, restore the
    // promised order properties before native form validation.
    document.addEventListener('submit', event => {
      if (this.closest('product-info')?.contains(event.target)) this.syncNativeDeferredGiftState();
    }, { ...options, capture: true });
    queueMicrotask(() => {
      const currentJourney = readGiftJourney();
      syncLegacyDeviceFlow(this.toggle.checked, currentJourney);
      document.dispatchEvent(new CustomEvent('auzi:gift-mode-changed', {
        detail: { active: this.toggle.checked, journey: currentJourney }
      }));
    });
  }

  update() {
    const enabled = this.toggle.checked;
    this.toggle.setAttribute('aria-expanded', String(enabled));
    const name = (this.nameInput?.value || '').trim();
    this.classList.toggle('auzi-gift-saved', enabled);

    const summary = this.querySelector('[data-gift-summary]');
    if (summary) summary.textContent = enabled ? this.dataset.selected : this.dataset.optional;

    if (this.titleElement) {
      const fallback = this.titleElement.dataset.defaultTitle || '';
      const giftTitle = this.dataset.giftTitle || fallback;
      const giftForTemplate = this.dataset.giftFor || giftTitle;
      this.titleElement.textContent = enabled ? (name ? giftForTemplate.replace('__NAME__', name) : giftTitle) : fallback;
    }

    const fields = this.querySelector('[data-gift-fields]');
    if (fields) fields.hidden = !enabled;
    [this.nameInput, this.messageInput].filter(Boolean).forEach(input => { input.disabled = !enabled; });
  }

  save() {
    this.update();
    const draft = {
      gift: this.toggle.checked,
      name: this.nameInput?.value || '',
      message: this.messageInput?.value || '',
      open: this.toggle.checked
    };
    giftDrafts.set(this.dataset.product, draft);

    const journey = readGiftJourney();
    const nextJourney = draft.gift
      ? {
          ...(journey || {}),
          active: true,
          knowsDevice: journey?.knowsDevice === true,
          family: journey?.family || 'hearing',
          device: journey?.knowsDevice ? journey.device : null,
          recipientName: draft.name,
          message: draft.message,
          updatedAt: Date.now()
        }
      : null;

    writeGiftJourney(nextJourney);
    syncLegacyDeviceFlow(draft.gift, nextJourney);

    if (!draft.gift) {
      document.dispatchEvent(new CustomEvent('auzi:gift-mode-cleared'));
    }

    document.dispatchEvent(new CustomEvent('auzi:gift-mode-changed', {
      detail: { active: draft.gift, journey: nextJourney }
    }));
    queueMicrotask(() => this.syncNativeDeferredGiftState());
  }

  syncNativeDeferredGiftState() {
    // Product templates use their native selector; keep the gift journey in
    // that *actual* slot rather than relying on the retired unified component.
    const info = this.closest('product-info');
    const native = info?.querySelector('[data-auzi-cover-device], [data-auzi-cochlear-device]');
    if (!native || !this.toggle) return;

    const family = native.hasAttribute('data-auzi-cochlear-device') ? 'cochlear' : 'hearing';
    const params = new URLSearchParams(window.location.search);
    const journey = readGiftJourney();
    const explicitUnknown = params.get('gift') === '1' &&
      params.get('gift_known') === '0' &&
      (!params.get('gift_family') || params.get('gift_family') === family);
    const inheritedUnknown = journey?.active === true &&
      journey.knowsDevice === false && journey.family === family;
    const editing = native.dataset.auziDeferredEditing === 'true' || journey?.editing === true;
    const showDeferred = this.toggle.checked && !editing && (explicitUnknown || inheritedUnknown);

    const existing = native.querySelector('[data-auzi-native-deferred-banner]');
    if (!showDeferred) {
      native.classList.remove('auzi-native-gift-deferred');
      if (existing) existing.hidden = true;
      if (!this.toggle.checked) {
        native.dataset.auziDeferredEditing = '';
        if (native.dataset.auziDeferredApplied === 'true') {
          // Cochlear's native gift-mode-changed handler restores its chooser;
          // clicking its gift-change button here would reactivate the gift session.
          if (family === 'hearing') native.querySelector('[data-device-later-change]')?.click();
          native.dataset.auziDeferredApplied = '';
        }
      }
      return;
    }

    const field = selector => native.querySelector(selector);
    const assign = (selector, value) => { const input = field(selector); if (input) { input.disabled = false; input.value = value; } };
    const later = 'To be provided after order';
    if (family === 'cochlear') {
      assign('[data-ci-manufacturer]', later);
      assign('[data-ci-model-value]', later);
      assign('[data-ci-image-value]', '');
      assign('[data-ci-confirmation]', 'Device details to be supplied after order');
    } else {
      assign('[data-device-manufacturer]', later);
      assign('[data-device-name]', later);
      assign('[data-device-image]', '');
      assign('[data-device-confirmation]', 'Device details to be supplied after order');
    }
    native.dataset.auziDeferredApplied = 'true';
    native.classList.add('auzi-native-gift-deferred');

    if (!existing) {
      const banner = document.createElement('div');
      banner.className = 'auzi-native-gift-notice';
      banner.dataset.auziNativeDeferredBanner = '';
      const message = document.createElement('span');
      message.className = 'auzi-native-gift-notice__message';
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tick.setAttribute('viewBox', '0 0 20 20');
      tick.setAttribute('fill', 'none');
      tick.setAttribute('aria-hidden', 'true');
      const tickPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tickPath.setAttribute('d', 'm4 10 4.1 4.1L16 5.5');
      tickPath.setAttribute('stroke', 'currentColor');
      tickPath.setAttribute('stroke-width', '1.7');
      tickPath.setAttribute('stroke-linecap', 'round');
      tickPath.setAttribute('stroke-linejoin', 'round');
      tick.appendChild(tickPath);
      const messageText = document.createElement('span');
      messageText.textContent = this.dataset.deviceLaterText || 'Device details can be shared after ordering';
      message.append(tick, messageText);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'auzi-native-gift-notice__action';
      const buttonText = document.createElement('span');
      buttonText.textContent = this.dataset.addDeviceText || 'Share device info';
      const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      arrow.setAttribute('viewBox', '0 0 20 20');
      arrow.setAttribute('fill', 'none');
      arrow.setAttribute('aria-hidden', 'true');
      const arrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      arrowPath.setAttribute('d', 'M4.5 10h10m-4-4 4 4-4 4');
      arrowPath.setAttribute('stroke', 'currentColor');
      arrowPath.setAttribute('stroke-width', '1.3');
      arrowPath.setAttribute('stroke-linecap', 'round');
      arrowPath.setAttribute('stroke-linejoin', 'round');
      arrow.appendChild(arrowPath);
      button.append(buttonText, arrow);
      button.addEventListener('click', () => {
        native.dataset.auziDeferredEditing = 'true';
        native.dataset.auziDeferredApplied = '';
        native.classList.remove('auzi-native-gift-deferred');
        banner.hidden = true;
        const current = readGiftJourney() || {};
        writeGiftJourney({
          ...current, active: true, knowsDevice: false, editing: true,
          family, device: null, updatedAt: Date.now()
        });
        try {
          sessionStorage.removeItem('auzi.device.send_later.v1');
          sessionStorage.removeItem('auzi.cochlear.send_later.v2');
        } catch (_) {}
        const change = native.querySelector(family === 'cochlear' ? '[data-ci-gift-change]' : '[data-device-later-change]');
        change?.click();
        for (const selector of family === 'cochlear'
          ? ['[data-ci-manufacturer]', '[data-ci-model-value]', '[data-ci-confirmation]']
          : ['[data-device-manufacturer]', '[data-device-name]', '[data-device-confirmation]']) {
          const input = field(selector);
          if (input?.value === later || input?.value === 'Device details to be supplied after order') input.value = '';
        }
        native.querySelector(family === 'cochlear' ? '[data-ci-brand-trigger]' : '[data-brand-trigger]')?.focus({ preventScroll: true });
      });
      banner.append(message, button);
      native.prepend(banner);
    } else {
      existing.hidden = false;
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
  }
}
if (!customElements.get('auzi-gift-option')) customElements.define('auzi-gift-option', AuziGiftOption);

if (!document.getElementById('auziGiftScrollGuard')) {
  const style = document.createElement('style');
  style.id = 'auziGiftScrollGuard';
  style.textContent = 'auzi-gift-option{display:block;overflow-anchor:none}auzi-gift-option details,auzi-gift-option summary,auzi-gift-option input,auzi-gift-option textarea{scroll-margin-top:110px}product-info:has(auzi-gift-option.auzi-gift-journey){overflow-anchor:none}';
  document.head.appendChild(style);
}


document.addEventListener('auzi:device-confirmed', event => {
  if (!event.detail?.gift) return;
  queueMicrotask(() => syncLegacyDeviceFlow(true, readGiftJourney()));
});




/* Deferred gift state belongs to the native PDP device chooser for both families. */
if (!document.getElementById('auziNativeDeferredGiftStyles')) {
  const style = document.createElement('style');
  style.id = 'auziNativeDeferredGiftStyles';
  style.textContent = `
    [data-auzi-cover-device].auzi-native-gift-deferred>[data-device-fields],
    [data-auzi-cover-device].auzi-native-gift-deferred>[data-carried-device],
    [data-auzi-cochlear-device].auzi-native-gift-deferred>[data-ci-fields],
    [data-auzi-cochlear-device].auzi-native-gift-deferred>[data-ci-entry],
    [data-auzi-cochlear-device].auzi-native-gift-deferred>[data-ci-later-selected],
    [data-auzi-cochlear-device].auzi-native-gift-deferred>[data-ci-gift-state],
    [data-auzi-cochlear-device].auzi-native-gift-deferred>.auzi-photo-optional{display:none!important}
    .auzi-native-gift-notice[hidden]{display:none!important}
    product-info .auzi-native-gift-notice{
      display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;
      column-gap:14px;row-gap:8px;width:100%;margin:12px 0 15px;
      padding:0;border:0;border-radius:0;background:transparent;
      box-shadow:none;color:#29784c;box-sizing:border-box
    }
    product-info .auzi-native-gift-notice__message{
      display:flex;align-items:center;gap:8px;min-width:0;text-align:left;
      color:#29784c;font-family:var(--font-heading-family,Garamond,Georgia,serif);
      font-size:17px;font-weight:700;line-height:1.35;letter-spacing:0
    }
    product-info .auzi-native-gift-notice__message svg{
      display:block;flex:0 0 19px;width:19px;height:19px;color:#29784c
    }
    product-info .auzi-native-gift-notice__action{
      display:inline-flex;align-items:center;justify-content:center;gap:7px;
      flex-shrink:0;justify-self:end;min-height:43px;padding:10px 15px;
      border:1px solid #c2ad90;border-radius:13px;background:#fffaf5;
      color:#574637;box-shadow:0 1px 2px rgba(74,52,25,.04);
      font-family:var(--font-body-family,Garamond,Georgia,serif);
      font-size:14px;font-weight:400;line-height:1.2;letter-spacing:0;
      text-decoration:none;white-space:nowrap;cursor:pointer;
      transition:background-color .15s,border-color .15s
    }
    product-info .auzi-native-gift-notice__action svg{
      display:block;flex:0 0 17px;width:17px;height:17px
    }
    product-info .auzi-native-gift-notice__action:hover{
      background:#f5ece0;border-color:#8f765a
    }
    product-info .auzi-native-gift-notice__action:focus-visible{
      outline:2px solid #8f765a;outline-offset:3px
    }
    @media(max-width:749px){
      product-info .auzi-native-gift-notice{column-gap:10px;margin:11px 0 14px}
      product-info .auzi-native-gift-notice__message{gap:6px;font-size:14px;line-height:1.3}
      product-info .auzi-native-gift-notice__message svg{flex-basis:16px;width:16px;height:16px}
      product-info .auzi-native-gift-notice__action{
        gap:5px;min-height:39px;padding:8px 10px;border-radius:11px;font-size:12px
      }
      product-info .auzi-native-gift-notice__action svg{flex-basis:14px;width:14px;height:14px}
    }
    @media(max-width:359px){
      product-info .auzi-native-gift-notice{grid-template-columns:1fr}
      product-info .auzi-native-gift-notice__action{grid-column:1;justify-self:start;margin-left:22px}
    }
    @media(prefers-reduced-motion:reduce){
      product-info .auzi-native-gift-notice__action{transition:none}
    }
  `;
  document.head.appendChild(style);
}
